"use client"

import { useMemo } from "react"
import { BarChart, Bar, XAxis, YAxis, Cell, LabelList } from "recharts"
import { IconFileOff } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"

import { useCurrency, getCurrencySymbol } from "@/hooks/use-currency"
import type { QuoteStats } from "@/types"
import { PRICE_MASK, pricesHidden } from "@/lib/price-visibility"

// Static module-scope colors (no strings — colors are static literals).
const STATUS_COLOR: Record<string, string> = {
  draft: "var(--chart-3)",
  sent: "var(--chart-1)",
  accepted: "var(--chart-2)",
  rejected: "var(--destructive)",
  expired: "var(--muted-foreground)",
}

function formatCurrencyAbbreviated(value: number | null | undefined, currency: string): string {
  if (pricesHidden()) return PRICE_MASK
  const numValue = Number(value) || 0
  const symbol = getCurrencySymbol(currency)
  if (numValue >= 1000000) {
    return `${symbol}${(numValue / 1000000).toFixed(1)}M`
  }
  if (numValue >= 1000) {
    return `${symbol}${(numValue / 1000).toFixed(1)}K`
  }
  return `${symbol}${numValue.toFixed(0)}`
}

interface QuotesFunnelChartProps {
  data: QuoteStats | null
  isLoading: boolean
}

export function QuotesFunnelChart({ data, isLoading }: QuotesFunnelChartProps) {
  const t = useTranslations("dashboard")
  const { currency } = useCurrency()

  // Static-literal switch resolver — never t(statusVar) per T-72-01.
  const statusLabel = (key: string): string => {
    switch (key) {
      case "draft":
        return t("charts.quotesFunnel.status.draft")
      case "sent":
        return t("charts.quotesFunnel.status.sent")
      case "accepted":
        return t("charts.quotesFunnel.status.accepted")
      case "rejected":
        return t("charts.quotesFunnel.status.rejected")
      case "expired":
        return t("charts.quotesFunnel.status.expired")
      default:
        return key
    }
  }

  const chartData = useMemo(() => {
    if (!data?.by_status) return []
    // Display in funnel order
    return [
      { name: "draft", value: data.by_status.draft ?? 0, fill: STATUS_COLOR.draft },
      { name: "sent", value: data.by_status.sent ?? 0, fill: STATUS_COLOR.sent },
      { name: "accepted", value: data.by_status.accepted ?? 0, fill: STATUS_COLOR.accepted },
      { name: "rejected", value: data.by_status.rejected ?? 0, fill: STATUS_COLOR.rejected },
      { name: "expired", value: data.by_status.expired ?? 0, fill: STATUS_COLOR.expired },
    ].filter(item => item.value > 0)
  }, [data])

  const chartConfig: ChartConfig = {
    draft: { label: t("charts.quotesFunnel.status.draft"), color: STATUS_COLOR.draft },
    sent: { label: t("charts.quotesFunnel.status.sent"), color: STATUS_COLOR.sent },
    accepted: { label: t("charts.quotesFunnel.status.accepted"), color: STATUS_COLOR.accepted },
    rejected: { label: t("charts.quotesFunnel.status.rejected"), color: STATUS_COLOR.rejected },
    expired: { label: t("charts.quotesFunnel.status.expired"), color: STATUS_COLOR.expired },
  }

  const totalSentValue = data?.total_amount?.sent ?? 0

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("charts.quotesFunnel.title")}>
        <div className="h-[200px] flex flex-col justify-center space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-5 flex-1 rounded" />
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center gap-1 mt-2 pt-2 border-t">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-6 w-24" />
        </div>
      </ApolloPanel>
    )
  }

  if (!data || chartData.length === 0) {
    return (
      <ApolloPanel eyebrow={t("charts.quotesFunnel.title")}>
        <div className="flex flex-col items-center justify-center h-[200px] text-center">
          <div className="rounded-full bg-muted p-3 mb-3">
            <IconFileOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("charts.quotesFunnel.empty.title")}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {t("charts.quotesFunnel.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel eyebrow={t("charts.quotesFunnel.title")}>
      <ChartContainer config={chartConfig} className="h-[200px] w-full">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ left: 0, right: 40, top: 8, bottom: 8 }}
        >
          <YAxis
            dataKey="name"
            type="category"
            tickLine={false}
            axisLine={false}
            width={70}
            tickFormatter={(value) => statusLabel(value)}
            tick={{ fontSize: 12 }}
          />
          <XAxis type="number" hide />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(value, name) => (
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: STATUS_COLOR[name as string] }}
                    />
                    <span className="text-muted-foreground">
                      {statusLabel(name as string)}
                    </span>
                    <span className="font-medium ml-auto">{value}</span>
                  </div>
                )}
              />
            }
          />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={20}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              className="fill-foreground"
              fontSize={12}
              offset={8}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
      <div className="flex flex-col items-center gap-1 mt-2 pt-2 border-t">
        <span className="text-xs text-muted-foreground">{t("charts.quotesFunnel.totalSentValue")}</span>
        <span className="font-serif text-[26px] leading-none tracking-[-0.01em] tabular-nums text-foreground">
          {formatCurrencyAbbreviated(totalSentValue, currency)}
        </span>
      </div>
    </ApolloPanel>
  )
}
